import leadlovers from '../services/leadlovers';
import { FormStructure, FormFields, FormField } from '~/types/form.types';
import { convertToKey } from '~/utils';
import {
  getMachineTypeRules,
  isFieldRequired,
  isFieldRemovable
} from '~/utils/machineTypeUtils';

interface ILeadLoversResponse<T> {
  Items: T[];
  StatusCode: number;
  Message: string;
  Exception: string;
}

interface ILeadLoversDynamicField {
  Id: number;
  Type: string;
  Status: string;
  Name: string;
  Label: string;
  Tag: string;
  RegisterDate: Date;
}

interface ILeadLoversDynamicFieldOption {
  Id: number;
  DynamicFieldId: number;
  Status: string;
  Value: string;
  Score: number;
  RegisterDate: Date;
}

type FieldType =
  | 'email'
  | 'radio'
  | 'checkbox'
  | 'select'
  | 'tel'
  | 'textarea'
  | 'text';

export type NativeFieldType = FieldType | 'date' | 'cpf' | 'cnpj' | 'cpf_cnpj';

const fieldsWithOptions: FieldType[] = ['checkbox', 'radio', 'select'];

class FormsService {
  private convertFields = (data: Map<string, FormField>): FormFields => {
    const obj: FormFields = {};
    data.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  };

  public getStaticFormByMachineType(
    machineType?: string | number
  ): FormStructure {
    const rules = getMachineTypeRules(machineType || 1);

    const baseFields = {
      sck: {
        canBeRemovedFromLayout: false,
        removeFromLayout: true,
        attributes: {
          id: 'sck',
          name: 'sck',
          value: ''
        },
        type: 'hidden' as const
      },
      nome: {
        type: 'text' as const,
        label: 'Nome',
        removeFromLayout: true,
        canBeRemovedFromLayout: true,
        attributes: {
          name: 'name',
          placeholder: 'Seu nome',
          itemtype: 'text' as const
        }
      },
      email: {
        type: 'email' as const,
        label: 'E-mail',
        removeFromLayout: rules.autoAddField !== 'email',
        canBeRemovedFromLayout: rules.emailRemovable,
        attributes: {
          name: 'email',
          required: rules.emailRequired,
          placeholder: 'Seu e-mail',
          itemtype: 'email' as const
        }
      },
      telefone: {
        type: 'tel' as const,
        label: 'Telefone',
        removeFromLayout: rules.autoAddField !== 'phone',
        canBeRemovedFromLayout: rules.phoneRemovable,
        attributes: {
          name: 'phone',
          required: rules.phoneRequired,
          placeholder: 'Telefone - (99) 99999-9999',
          itemtype: 'tel' as const
        }
      },
      data: {
        type: 'text' as const,
        label: 'Data de Nascimento',
        removeFromLayout: true,
        canBeRemovedFromLayout: true,
        attributes: {
          name: 'birthDate',
          placeholder: 'Data de nascimento',
          itemtype: 'date' as const
        }
      },
      empresa: {
        type: 'text' as const,
        label: 'Empresa',
        removeFromLayout: true,
        canBeRemovedFromLayout: true,
        attributes: {
          name: 'company',
          placeholder: 'Nome da empresa',
          itemtype: 'text' as const
        }
      },
      cidade: {
        type: 'text' as const,
        label: 'Cidade',
        removeFromLayout: true,
        canBeRemovedFromLayout: true,
        attributes: {
          name: 'city',
          placeholder: 'Nome da cidade',
          itemtype: 'text' as const
        }
      },
      estado: {
        type: 'text' as const,
        label: 'Estado',
        removeFromLayout: true,
        canBeRemovedFromLayout: true,
        attributes: {
          name: 'state',
          placeholder: 'Sigla do estado',
          itemtype: 'text' as const
        }
      },
      mensagem: {
        type: 'textarea' as const,
        label: 'Mensagem',
        removeFromLayout: true,
        canBeRemovedFromLayout: true,
        attributes: {
          name: 'message',
          placeholder: 'Digite uma mensagem',
          cols: 3,
          itemtype: 'textarea' as const
        }
      },
      botao: {
        type: 'submit' as const,
        label: '',
        canBeRemovedFromLayout: false,
        attributes: {
          value: 'ENVIAR',
          name: 'submit_button'
        }
      }
    };

    const mainFieldLayout =
      rules.autoAddField === 'phone' ? ['telefone'] : ['email'];

    const layout: string[][] = [
      ['sck'],
      mainFieldLayout,
      ['nome', rules.autoAddField === 'phone' ? 'email' : 'telefone'],
      ['data', 'empresa'],
      ['cidade', 'estado'],
      ['mensagem'],
      ['botao']
    ];

    return {
      fields: baseFields,
      layout,
      title: 'static'
    };
  }

  public async getDynamicForm(
    token: string,
    machineType?: string | number
  ): Promise<FormStructure | undefined> {
    try {
      const response = await leadlovers.get<
        ILeadLoversResponse<ILeadLoversDynamicField>
      >(`DynamicFields?token=${token}`);
      if (response.status === 200) {
        const rules = getMachineTypeRules(machineType || 1);
        const firstEmailField = response.data.Items.find(
          x => x.Type === 'E-mail'
        )?.Id;
        const firstPhoneField = response.data.Items.find(
          x => x.Type === 'Telefone'
        )?.Id;

        const mainFieldId =
          rules.requiredField === 'phone' ? firstPhoneField : firstEmailField;
        const array = await Promise.all(
          response.data.Items.map(
            async (i): Promise<[string, FormField]> => {
              const fieldType = this.mapType(i.Type);
              const options: string[] = fieldsWithOptions.includes(fieldType)
                ? await this.getDynamicFieldOptions(i.Id, token)
                : [];
              return [
                convertToKey(`${i.Name} ${i.Id}`),
                {
                  type: fieldType,
                  label: i.Name,
                  removeFromLayout: i.Id !== mainFieldId,
                  canBeRemovedFromLayout: isFieldRemovable(fieldType, rules),
                  options,
                  attributes: {
                    key: convertToKey(i.Name),
                    name: `llfield${i.Id}`,
                    cols: fieldType === 'textarea' ? 3 : undefined,
                    placeholder: i.Label,
                    required: isFieldRequired(fieldType, rules),
                    itemtype: this.mapNativeType(i.Type)
                  }
                }
              ];
            }
          )
        );

        const map = new Map(array);
        const fields = this.convertFields(map);
        const layout: string[][] = response.data.Items.map(item => [
          convertToKey(`${item.Name} ${item.Id}`)
        ]);

        fields.button = {
          type: 'submit',
          label: '',
          canBeRemovedFromLayout: false,
          attributes: {
            value: 'ENVIAR',
            name: 'submit_button'
          }
        };

        return {
          fields,
          layout,
          title: 'dynamic'
        };
      }
    } catch (err) {
      console.error(err);
    }
    return undefined;
  }

  public async getDynamicFieldOptions(
    fieldId: number,
    token: string
  ): Promise<string[]> {
    try {
      const response = await leadlovers.get<
        ILeadLoversResponse<ILeadLoversDynamicFieldOption>
      >(`DynamicFieldOptions/${fieldId}?token=${token}`);
      if (response.status === 200) {
        return response.data.Items.map(x => x.Value);
      }
    } catch (err) {
      console.error('DynamicFieldOptions ERROR:', err);
    }
    return [];
  }

  private mapType(type: string): FieldType {
    switch (type) {
      case 'E-mail':
        return 'email';
      case 'Escolha Única (radio button)':
        return 'radio';
      case 'Multipla Escolha (checkbox)':
        return 'checkbox';
      case 'Seleção':
        return 'select';
      case 'Telefone':
        return 'tel';
      case 'Texto Grande':
        return 'textarea';
      default:
        return 'text';
    }
  }

  private mapNativeType(type: string): NativeFieldType {
    switch (type) {
      case 'E-mail':
        return 'email';
      case 'Escolha Única (radio button)':
        return 'radio';
      case 'Multipla Escolha (checkbox)':
        return 'checkbox';
      case 'Seleção':
        return 'select';
      case 'Telefone':
        return 'tel';
      case 'Texto Grande':
        return 'textarea';
      case 'Data':
        return 'date';
      case 'CPF':
        return 'cpf';
      case 'CNPJ':
        return 'cnpj';
      case 'CPF/CNPJ':
        return 'cpf_cnpj';
      default:
        return 'text';
    }
  }
}

export default new FormsService();
